import { useState, useCallback, useRef, useEffect } from "react";
import { getUrl } from "aws-amplify/storage";
import { useUserModule } from "../../../contexts/UserModuleContext";
import "../../../css/UserProfileTabs.css";

interface MediaFilesTabProps {
    user: any;
    readOnly: boolean;
}

interface FileWithUrl {
    id: string;
    fileName: string;
    s3Path: string;
    fileSize: number;
    uploadedBy: string;
    uploadedByName: string;
    uploadedAt?: string | null;
    url?: string;
}

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "avi", "webm"];
const PDF_EXTENSIONS = ["pdf"];

function getFileExtension(fileName: string): string {
    return fileName.split(".").pop()?.toLowerCase() || "";
}

function getFileNameWithoutExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf(".");
    if (lastDot === -1) return fileName;
    return fileName.substring(0, lastDot);
}

function isImageFile(fileName: string): boolean {
    return IMAGE_EXTENSIONS.includes(getFileExtension(fileName));
}

function isVideoFile(fileName: string): boolean {
    return VIDEO_EXTENSIONS.includes(getFileExtension(fileName));
}

function isPdfFile(fileName: string): boolean {
    return PDF_EXTENSIONS.includes(getFileExtension(fileName));
}

function isPreviewable(fileName: string): boolean {
    return isImageFile(fileName) || isVideoFile(fileName) || isPdfFile(fileName);
}

/**
 * MediaFilesTab — File management tab for user profile.
 * Shows uploaded files with previews (for images), download, rename, and delete actions.
 * Clicking a file opens a preview modal (similar to training topic viewer).
 *
 * Validates: Requirements 10.1, 10.2, 10.3, 10.4
 */
export const MediaFilesTab = ({ user, readOnly }: MediaFilesTabProps) => {
    const { userFiles, uploadUserFile, deleteUserFile, renameUserFile } = useUserModule();
    const [isUploading, setIsUploading] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [filesWithUrls, setFilesWithUrls] = useState<FileWithUrl[]>([]);
    const [loadingUrls, setLoadingUrls] = useState(false);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [previewFile, setPreviewFile] = useState<FileWithUrl | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const renameInputRef = useRef<HTMLInputElement>(null);

    // Generate presigned URL for a single file
    const generateUrlForFile = useCallback(async (file: any): Promise<FileWithUrl> => {
        try {
            const result = await getUrl({ path: file.s3Path });
            return { ...file, url: result.url.toString() };
        } catch (err) {
            console.error(`Failed to get URL for ${file.fileName}:`, err);
            return { ...file, url: undefined };
        }
    }, []);

    // Generate presigned URLs for all files
    useEffect(() => {
        if (userFiles.length === 0) {
            setFilesWithUrls([]);
            return;
        }

        let cancelled = false;
        setLoadingUrls(true);

        const generateUrls = async () => {
            const results = await Promise.all(userFiles.map(generateUrlForFile));
            if (!cancelled) {
                setFilesWithUrls(results);
                setLoadingUrls(false);
            }
        };

        generateUrls();
        return () => { cancelled = true; };
    }, [userFiles, generateUrlForFile]);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file || !user?.id) return;

            setIsUploading(true);
            try {
                await uploadUserFile(user.id, file);
            } catch (err) {
                console.error("Upload failed:", err);
            } finally {
                setIsUploading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
        },
        [user?.id, uploadUserFile]
    );

    const handleDelete = useCallback(
        async (fileId: string, s3Path: string) => {
            try {
                await deleteUserFile(fileId, s3Path);
            } catch (err) {
                console.error("Delete failed:", err);
            } finally {
                setDeleteConfirmId(null);
            }
        },
        [deleteUserFile]
    );

    const handleDownload = async (url: string, fileName: string, s3Path: string) => {
        try {
            // Generate a download-specific URL with content-disposition: attachment
            const result = await getUrl({
                path: s3Path,
                options: {
                    expiresIn: 300,
                    useAccelerateEndpoint: false,
                },
            });
            const downloadUrl = result.url.toString();

            // Fetch as blob to force browser download dialog
            const response = await fetch(downloadUrl, { mode: "cors" });
            if (!response.ok) throw new Error("Fetch failed");
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } catch (err) {
            // Final fallback: open in new tab
            console.error("Download failed, opening in new tab:", err);
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    // Rename: only edits the name part, preserves extension
    const handleStartRename = (file: FileWithUrl) => {
        setRenamingId(file.id);
        setRenameValue(getFileNameWithoutExtension(file.fileName));
        setTimeout(() => renameInputRef.current?.select(), 50);
    };

    const handleRenameSubmit = async (fileId: string) => {
        const trimmed = renameValue.trim();
        if (!trimmed) {
            setRenamingId(null);
            return;
        }
        const originalFile = userFiles.find(f => f.id === fileId);
        if (!originalFile) { setRenamingId(null); return; }

        const ext = getFileExtension(originalFile.fileName);
        const newFileName = ext ? `${trimmed}.${ext}` : trimmed;

        if (newFileName === originalFile.fileName) {
            setRenamingId(null);
            return;
        }

        try {
            await renameUserFile(fileId, newFileName);
        } catch (err) {
            console.error("Rename failed:", err);
        }
        setRenamingId(null);
    };

    const handleRenameKeyDown = (e: React.KeyboardEvent, fileId: string) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleRenameSubmit(fileId);
        } else if (e.key === "Escape") {
            setRenamingId(null);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatDate = (dateStr?: string | null): string => {
        if (!dateStr) return "—";
        try {
            return new Date(dateStr).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        } catch {
            return dateStr;
        }
    };

    const getFileIcon = (fileName: string): string => {
        const ext = getFileExtension(fileName);
        if (IMAGE_EXTENSIONS.includes(ext)) return "🖼️";
        if (PDF_EXTENSIONS.includes(ext)) return "📄";
        if (["doc", "docx"].includes(ext)) return "📝";
        if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
        if (VIDEO_EXTENSIONS.includes(ext)) return "🎬";
        if (["zip", "rar", "7z"].includes(ext)) return "📦";
        return "📎";
    };

    return (
        <div className="upt-media-tab">
            {/* Upload Button */}
            {!readOnly && (
                <div className="upt-media-actions">
                    <button
                        className="upt-btn-action"
                        onClick={handleUploadClick}
                        disabled={isUploading}
                    >
                        {isUploading ? "Uploading..." : "+ Upload File"}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.mp4,.mov,.zip"
                    />
                </div>
            )}

            {/* File List */}
            <div className="upt-section" style={{ gridColumn: "1 / -1" }}>
                <h3 className="upt-section-title">Uploaded Files</h3>
                {loadingUrls && userFiles.length > 0 && filesWithUrls.length === 0 && (
                    <div className="upt-empty-state">Loading files...</div>
                )}
                {userFiles.length === 0 ? (
                    <div className="upt-empty-state">
                        No files uploaded yet.{" "}
                        {!readOnly && "Click \"+ Upload File\" to add documents, images, or other files."}
                    </div>
                ) : (
                    <div className="upt-media-file-list">
                        {filesWithUrls.map((file) => (
                            <div key={file.id} className="upt-media-file-row">
                                {/* Preview / Icon — clickable to open preview */}
                                <div
                                    className="upt-media-file-icon"
                                    onClick={() => file.url && isPreviewable(file.fileName) && setPreviewFile(file)}
                                    style={{ cursor: isPreviewable(file.fileName) && file.url ? "pointer" : "default" }}
                                >
                                    {isImageFile(file.fileName) && file.url ? (
                                        <img
                                            src={file.url}
                                            alt={file.fileName}
                                            className="upt-media-file-thumbnail"
                                        />
                                    ) : (
                                        <span className="upt-media-file-emoji">
                                            {getFileIcon(file.fileName)}
                                        </span>
                                    )}
                                </div>
                                <div className="upt-media-file-info">
                                    {renamingId === file.id ? (
                                        <div className="upt-media-rename-row">
                                            <input
                                                ref={renameInputRef}
                                                type="text"
                                                className="upt-media-rename-input"
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onKeyDown={(e) => handleRenameKeyDown(e, file.id)}
                                                onBlur={() => handleRenameSubmit(file.id)}
                                                autoFocus
                                            />
                                            <span className="upt-media-rename-ext">
                                                .{getFileExtension(file.fileName)}
                                            </span>
                                        </div>
                                    ) : (
                                        <span
                                            className="upt-media-file-name"
                                            onClick={() => file.url && isPreviewable(file.fileName) && setPreviewFile(file)}
                                            style={{ cursor: isPreviewable(file.fileName) && file.url ? "pointer" : "default" }}
                                        >
                                            {file.fileName}
                                        </span>
                                    )}
                                    <span className="upt-media-file-meta">
                                        {formatDate(file.uploadedAt)} · {file.uploadedByName} · {formatFileSize(file.fileSize)}
                                    </span>
                                </div>
                                <div className="upt-media-file-actions">
                                    {deleteConfirmId === file.id ? (
                                        <div className="upt-media-delete-confirm">
                                            <span>Delete?</span>
                                            <button
                                                className="upt-btn-confirm-delete"
                                                onClick={() => handleDelete(file.id, file.s3Path)}
                                            >
                                                Yes
                                            </button>
                                            <button
                                                className="upt-btn-cancel-delete"
                                                onClick={() => setDeleteConfirmId(null)}
                                            >
                                                No
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            {file.url && (
                                                <button
                                                    className="upt-btn-file-action download"
                                                    onClick={() => handleDownload(file.url!, file.fileName, file.s3Path)}
                                                    title="Download file"
                                                >
                                                    ⬇️
                                                </button>
                                            )}
                                            {!readOnly && (
                                                <button
                                                    className="upt-btn-file-action rename"
                                                    onClick={() => handleStartRename(file)}
                                                    title="Rename file"
                                                >
                                                    ✏️
                                                </button>
                                            )}
                                            {!readOnly && (
                                                <button
                                                    className="upt-btn-file-action delete"
                                                    onClick={() => setDeleteConfirmId(file.id)}
                                                    title="Delete file"
                                                >
                                                    🗑️
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            {previewFile && previewFile.url && (
                <div className="upt-preview-overlay" onClick={() => setPreviewFile(null)}>
                    <div className="upt-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="upt-preview-header">
                            <h3 className="upt-preview-title">{previewFile.fileName}</h3>
                            <button className="upt-preview-close" onClick={() => setPreviewFile(null)} aria-label="Close">
                                ×
                            </button>
                        </div>
                        <div className="upt-preview-content">
                            {isImageFile(previewFile.fileName) && (
                                <img
                                    src={previewFile.url}
                                    alt={previewFile.fileName}
                                    className="upt-preview-image"
                                />
                            )}
                            {isVideoFile(previewFile.fileName) && (
                                <video
                                    controls
                                    preload="metadata"
                                    src={previewFile.url}
                                    className="upt-preview-video"
                                >
                                    Your browser does not support the video tag.
                                </video>
                            )}
                            {isPdfFile(previewFile.fileName) && (
                                <iframe
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewFile.url)}&embedded=true`}
                                    title={previewFile.fileName}
                                    className="upt-preview-pdf"
                                />
                            )}
                        </div>
                        <div className="upt-preview-footer">
                            <span className="upt-preview-meta">
                                {formatFileSize(previewFile.fileSize)} · Uploaded {formatDate(previewFile.uploadedAt)} by {previewFile.uploadedByName}
                            </span>
                            <button
                                className="upt-btn-action"
                                onClick={() => handleDownload(previewFile.url!, previewFile.fileName, previewFile.s3Path)}
                            >
                                ⬇️ Download
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
